// src/Pages/Estabilishment/BannerForm/index.tsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { CircularProgress } from "@mui/material";
import {
  postEstablishmentBanner,
  putEstablishmentBanner,
  getBannerIDEstabilishment,
  uploadEstablishmentBannerImage,
  postEstablishmentBannerForm,
  putEstablishmentBannerForm,
} from "../../../Services/Banner";
import { api } from "../../../Services/Api";
import { getEstablishmentDashboard } from "../../../Services/EstablishmentDashboard";
import type { IRestDashboard } from "../../../Services/EstablishmentDashboard";
import { useT } from "../../../i18n";
import type { TFunction } from "../../../i18n";

// Busca imagem do banner via Axios com token (VichUploader retorna caminho relativo)
/**
 * E-mail para quem quer que o time Pertalks crie a arte do banner.
 * Definir REACT_APP_ART_EMAIL no .env quando o endereço final for definido.
 */
const ART_EMAIL =
  process.env.REACT_APP_ART_EMAIL ||
  process.env.REACT_APP_SUPPORT_EMAIL ||
  "places@pertalks.com";

const BannerImage: React.FC<{ filename: string; onRemove: () => void }> = ({
  filename,
  onRemove,
}) => {
  const t = useT();

  const src = filename.startsWith("http")
    ? filename
    : `${process.env.REACT_APP_DIGITAL_ASSETS_URL ?? ""}${filename}`;

  return (
    <div style={{ marginBottom: 10 }}>
      <img
        src={src}
        alt={t("Banner")}
        style={{
          width: "100%",
          maxWidth: 420,
          height: 150,
          objectFit: "cover",
          borderRadius: 8,
          border: "1.5px solid #e8e8e8",
          display: "block",
        }}
      />
      <button
        onClick={onRemove}
        style={{
          display: "block",
          marginTop: 6,
          background: "none",
          border: "none",
          color: "#ef4444",
          fontSize: 12,
          cursor: "pointer",
          padding: 0,
        }}
      >
        {t("Remover imagem")}
      </button>
    </div>
  );
};

interface IBanner {
  id: string;
  name: string;
  title: string | null;
  isActive: boolean;
  displayOrder: number;
  line: number | null;
  filename: string | null;
  url: string | null;
  internalUrl: boolean;

  // ── Aprovação da arte (planos Showcase Pulse/Wave) ──────────────────────
  artStatus?: "pending" | "approved" | "rejected";
  artApprovedAt?: string | null;
  artRejectionReason?: string | null;
}

// ─── Icons ────────────────────────────────────────────────────────────────────
const ic = (p: React.ReactNode, s = 16) => (
  <svg
    width={s}
    height={s}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {p}
  </svg>
);
const IconUpload = () =>
  ic(
    <>
      <polyline points="16 16 12 12 8 16" />
      <line x1="12" y1="12" x2="12" y2="21" />
      <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
    </>,
  );
const IconChevL = () =>
  ic(
    <>
      <polyline points="15 18 9 12 15 6" />
    </>,
  );
const IconCheck = () =>
  ic(
    <>
      <polyline points="20 6 9 17 4 12" />
    </>,
  );

// ─── Especificações do banner ────────────────────────────────────────────────
// Fonte única: o texto exibido ao usuário e a validação usam estas constantes,
// para nunca divergirem.
const BANNER_WIDTH  = 420;
const BANNER_HEIGHT = 150;

/** Limite de peso. Banner de 420x150 costuma ficar entre 30 e 80 KB, então
 *  500 KB é folgado e ainda protege o carregamento do app. */
const BANNER_MAX_KB = 500;

/** WebP incluído por ter compressão melhor e suporte universal hoje.
 *  GIF ficou de fora — ver observação na conversa. */
const BANNER_FORMATS = ["image/jpeg", "image/png", "image/webp"];
const BANNER_FORMATS_LABEL = "JPG, PNG ou WebP";

// ─── Validation ───────────────────────────────────────────────────────────────
const validateImage = (file: File, t: TFunction): Promise<string | null> =>
  new Promise((resolve) => {
    if (!BANNER_FORMATS.includes(file.type)) {
      resolve(
        t("Formato inválido. Envie um arquivo {formatos}.", {
          formatos: t(BANNER_FORMATS_LABEL),
        }),
      );
      return;
    }

    // Peso: evita deixar o carregamento do app lento
    if (file.size > BANNER_MAX_KB * 1024) {
      const kb = Math.round(file.size / 1024);
      resolve(
        t("Arquivo muito pesado ({kb} KB). O limite é {max} KB.", {
          kb,
          max: BANNER_MAX_KB,
        }),
      );
      return;
    }

    const img = new window.Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      if (img.width !== BANNER_WIDTH || img.height !== BANNER_HEIGHT) {
        resolve(
          t("A imagem deve ter exatamente {w}x{h}px (a enviada tem {rw}x{rh}px).", {
            w: BANNER_WIDTH,
            h: BANNER_HEIGHT,
            rw: img.width,
            rh: img.height,
          }),
        );
      } else {
        resolve(null);
      }
    };
    img.onerror = () => resolve(t("Não foi possível ler a imagem."));
    img.src = URL.createObjectURL(file);
  });

// ─── Field Components ─────────────────────────────────────────────────────────
const Label: React.FC<{ children: React.ReactNode; required?: boolean }> = ({
  children,
  required,
}) => (
  <label
    style={{
      fontSize: 12,
      fontWeight: 600,
      color: "#555",
      display: "block",
      marginBottom: 5,
    }}
  >
    {children}
    {required && <span style={{ color: "#ef4444", marginLeft: 2 }}>*</span>}
  </label>
);

const Input: React.FC<
  React.InputHTMLAttributes<HTMLInputElement> & { error?: string }
> = ({ error, style, ...props }) => (
  <div>
    <input
      {...props}
      style={{
        width: "100%",
        padding: "9px 12px",
        border: `1.5px solid ${error ? "#ef4444" : "#e8e8e8"}`,
        borderRadius: 8,
        fontSize: 13,
        color: "#333",
        outline: "none",
        boxSizing: "border-box",
        background: "#fff",
        ...style,
      }}
    />
    {error && (
      <span
        style={{
          fontSize: 11,
          color: "#ef4444",
          marginTop: 3,
          display: "block",
        }}
      >
        {error}
      </span>
    )}
  </div>
);

const SelectField: React.FC<{
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}> = ({ value, onChange, options }) => (
  <div style={{ position: "relative" }}>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: "100%",
        padding: "9px 12px",
        border: "1.5px solid #e8e8e8",
        borderRadius: 8,
        fontSize: 13,
        color: "#333",
        outline: "none",
        appearance: "none",
        background: "#fff",
        cursor: "pointer",
      }}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
    <svg
      style={{
        position: "absolute",
        right: 10,
        top: "50%",
        transform: "translateY(-50%)",
        pointerEvents: "none",
        color: "#aaa",
      }}
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  </div>
);

// ─── Toggle ───────────────────────────────────────────────────────────────────
const Toggle: React.FC<{
  checked: boolean;
  onChange: (v: boolean) => void;
  label?: string;
}> = ({ checked, onChange, label }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
    <button
      onClick={() => onChange(!checked)}
      style={{
        width: 42,
        height: 24,
        borderRadius: 999,
        background: checked ? "#00b6bc" : "#d1d5db",
        border: "none",
        cursor: "pointer",
        position: "relative",
        transition: "background 0.2s",
        flexShrink: 0,
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 3,
          left: checked ? 20 : 3,
          width: 18,
          height: 18,
          borderRadius: "50%",
          background: "#fff",
          transition: "left 0.2s",
          boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
        }}
      />
    </button>
    {label && (
      <span style={{ fontSize: 13, color: "#333", fontWeight: 600 }}>
        {label}
      </span>
    )}
  </div>
);

// ─── Main Form ────────────────────────────────────────────────────────────────
interface IFormValues {
  name: string;
  displayOrder: string;
  title: string;
  tipoLink: "externo" | "interno";
  url: string;
  isActive: boolean;
  image: File | null;
  existingFilename: string | null;
}

const INITIAL: IFormValues = {
  name: "",
  displayOrder: "1",
  title: "",
  tipoLink: "externo",
  url: "",
  isActive: true,
  image: null,
  existingFilename: null,
};

export const BannerForm = () => {
  const t = useT();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const location = useLocation();
  const fileRef = useRef<HTMLInputElement>(null);
  const isEdit = Boolean(id);

  // status da análise da arte, exibido no modo edição
  const [art, setArt] = useState<{
    status?: string;
    reason?: string | null;
    approvedAt?: string | null;
  } | null>(null);

  const [values, setValues] = useState<IFormValues>(INITIAL);
  const [errors, setErrors] = useState<
    Partial<Record<keyof IFormValues, string>>
  >({});
  const [saving, setSaving] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [imgError, setImgError] = useState("");

  // ── Plano da unidade ───────────────────────────────────────────────────
  // Define se a URL é obrigatória: no Connection o banner pode não levar a
  // lugar nenhum; no Showcase (Pulse/Wave) o link continua obrigatório.
  // O formulário espera essa resposta antes de renderizar — sem isso, quem
  // salvasse rápido demais cairia na regra errada.
  const [planType, setPlanType] = useState("");
  const [planLoading, setPlanLoading] = useState(true);

  useEffect(() => {
    getEstablishmentDashboard()
      .then((res) => {
        setPlanType((res as IRestDashboard).data.data.planType ?? "");
      })
      // falha na consulta mantém a regra mais restritiva
      .catch(() => setPlanType(""))
      .finally(() => setPlanLoading(false));
  }, []);

  const urlOpcional = planType === "connection";

  // Carrega banner existente (edit ou repost)
  useEffect(() => {
    const repostData = (location.state as any)?.repost as IBanner | undefined;
    if (repostData) {
      setValues({
        name: `${repostData.name} ${t("(cópia)")}`,
        displayOrder: String(repostData.displayOrder ?? 1),
        title: repostData.title ?? "",
        tipoLink: repostData.internalUrl ? "interno" : "externo",
        url: repostData.url ?? "",
        isActive: true,
        image: null,
        existingFilename: repostData.filename ?? null,
      });
      if (repostData.filename) setPreview(repostData.filename);
      return;
    }
    if (!isEdit || !id) return;
    setFetching(true);
    getBannerIDEstabilishment(id)
      .then((res: any) => {
        const b: IBanner = res.data.data;
        setValues({
          name: b.name,
          displayOrder: String(b.displayOrder ?? 1),
          title: b.title ?? "",
          tipoLink: b.internalUrl ? "interno" : "externo",
          url: b.url ?? "",
          isActive: b.isActive,
          image: null,
          existingFilename: b.filename ?? null,
        });
        if (b.filename) setPreview(b.filename);

        setArt({
          status:     b.artStatus,
          reason:     b.artRejectionReason,
          approvedAt: b.artApprovedAt,
        });
      })
      .catch(() => toast.error(t("Erro ao carregar banner.")))
      .finally(() => setFetching(false));
  }, [id, isEdit, location.state, t]);

  const set = (k: keyof IFormValues, v: any) => {
    setValues((prev) => ({ ...prev, [k]: v }));
    setErrors((prev) => ({ ...prev, [k]: undefined }));
  };

  const handleFile = async (file: File) => {
    setImgError("");
    const err = await validateImage(file, t);
    if (err) {
      setImgError(err);
      return;
    }
    set("image", file);
    const url = URL.createObjectURL(file);
    setPreview(url);
  };

  const validate = () => {
    const e: typeof errors = {};
    if (!values.name.trim()) e.name = t("Campo obrigatório");
    if (!urlOpcional && !values.url.trim()) e.url = t("Campo obrigatório");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);

    // FormData única — inclui imagem se selecionada
    const fd = new FormData();
    fd.append("name", values.name);
    // Pulse/Wave: ordenação definida pela Pertalks e link sempre externo.
    // Forçamos aqui para o valor não depender do que ficou no estado.
    fd.append("displayOrder", "1");
    fd.append("title", values.title || "");
    fd.append("url", values.url);
    fd.append("internalUrl", "n");
    fd.append("isActive", values.isActive ? "active" : "inactive");
    if (values.image) fd.append("uploadFile", values.image);

    const call =
      isEdit && id
        ? putEstablishmentBannerForm(id, fd)
        : postEstablishmentBannerForm(fd);

    call
      .then(() => {
        toast.success(t("Informações salvas com sucesso."));
        navigate("/establishment/banners");
      })
      .catch(() =>
        toast.error(
          t("Não foi possível salvar as informações. Por favor, tente novamente."),
        ),
      )
      .finally(() => setSaving(false));
  };

  if (fetching || planLoading)
    return (
      <div
        style={{ display: "flex", justifyContent: "center", paddingTop: 60 }}
      >
        <CircularProgress style={{ color: "#00b6bc" }} />
      </div>
    );

  return (
    <div style={{ maxWidth: 760 }}>
      <h2
        style={{
          margin: "0 0 6px",
          fontSize: 20,
          fontWeight: 700,
          color: "#111",
        }}
      >
        {isEdit ? t("Editar banner") : t("Cadastrar banner")}
      </h2>
      <p style={{ margin: "0 0 20px", fontSize: 13, color: "#888" }}>
        {t(
          "Preencha as informações abaixo para {acao} um banner. Ele será exibido na página do estabelecimento.",
          { acao: isEdit ? t("editar") : t("cadastrar") },
        )}
      </p>

      {/* ── Status da análise da arte ─────────────────────────────────────── */}
      {isEdit && art?.status === "rejected" && (
        <div style={{
          display: "flex", alignItems: "flex-start", gap: 10,
          background: "#fee2e2", border: "1px solid #fecaca",
          borderRadius: 12, padding: "14px 16px", marginBottom: 20,
        }}>
          <span style={{ fontSize: 18, lineHeight: 1 }}>⚠️</span>
          <div>
            <p style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 700, color: "#b91c1c" }}>
              {t("Sua arte precisa de ajuste")}
            </p>
            {art.reason && (
              <p style={{ margin: "0 0 6px", fontSize: 13, color: "#7f1d1d", lineHeight: 1.6 }}>
                {art.reason}
              </p>
            )}
            <p style={{ margin: 0, fontSize: 12, color: "#b91c1c", lineHeight: 1.6 }}>
              {t("Envie uma nova imagem abaixo. Não há limite de tentativas e o prazo contratado só começa a contar depois da aprovação.")}
            </p>
          </div>
        </div>
      )}

      {isEdit && art?.status === "pending" && (
        <div style={{
          display: "flex", alignItems: "flex-start", gap: 10,
          background: "#fff7e6", border: "1px solid #fde68a",
          borderRadius: 12, padding: "14px 16px", marginBottom: 20,
        }}>
          <span style={{ fontSize: 18, lineHeight: 1 }}>⏳</span>
          <p style={{ margin: 0, fontSize: 13, color: "#92400e", lineHeight: 1.6 }}>
            <strong>{t("Arte em análise.")}</strong>{" "}
            {t("Nossa equipe avalia em até 72 horas. Se você trocar a imagem agora, a análise recomeça do zero.")}
          </p>
        </div>
      )}

      {isEdit && art?.status === "approved" && (
        <div style={{
          display: "flex", alignItems: "flex-start", gap: 10,
          background: "#e6f7ef", border: "1px solid #a7f3d0",
          borderRadius: 12, padding: "14px 16px", marginBottom: 20,
        }}>
          <span style={{ fontSize: 18, lineHeight: 1 }}>✅</span>
          <p style={{ margin: 0, fontSize: 13, color: "#1a7a4a", lineHeight: 1.6 }}>
            <strong>{t("Arte aprovada e no ar")}</strong>
            {art.approvedAt &&
              ` ${t("desde {data}", {
                data: new Date(String(art.approvedAt).replace(" ", "T")).toLocaleDateString("pt-BR"),
              })}`}.
            {" "}
            {t("Atenção: trocar a imagem tira o banner do ar até a nova arte ser aprovada.")}
          </p>
        </div>
      )}

      {/* Row 1: Nome + Ordenação + Título
          Pulse/Wave não escolhem a ordenação — a coluna some do grid. */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 14,
          marginBottom: 16,
        }}
      >
        <div>
          <Label required>{t("Nome")}</Label>
          <Input
            placeholder={t("Digite um nome")}
            value={values.name}
            onChange={(e) => set("name", e.target.value)}
            error={errors.name}
          />
        </div>
        <div>
          <Label>{t("Título (opcional)")}</Label>
          <Input
            placeholder={t("Digite um título")}
            value={values.title}
            onChange={(e) => set("title", e.target.value)}
          />
        </div>
      </div>

      {/* Row 2: URL + Ativo
          O tipo de link não é mais escolhido pelo estabelecimento: todo banner
          usa link externo, e a ordenação é definida pela Pertalks. */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          gap: 14,
          marginBottom: 24,
          alignItems: "end",
        }}
      >
        <div>
          <Label required={!urlOpcional}>
            {t("URL")}
            {urlOpcional ? ` ${t("(opcional)")}` : ""}
          </Label>
          <Input
            placeholder={
              urlOpcional
                ? t("Opcional — deixe em branco se o banner não leva a lugar nenhum")
                : t("Digite a url que o usuário deverá ser redirecionado ao clicar no banner")
            }
            value={values.url}
            onChange={(e) => set("url", e.target.value)}
            error={errors.url}
          />
        </div>
        <div style={{ paddingBottom: 2 }}>
          <Toggle
            checked={values.isActive}
            onChange={(v) => set("isActive", v)}
            label={t("Ativo")}
          />
        </div>
      </div>

      {/* Image Upload */}
      <div style={{ marginBottom: 28 }}>
        <Label>{t("Imagem do banner")}</Label>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          style={{ display: "none" }}
          onChange={(e) => {
            if (e.target.files?.[0]) handleFile(e.target.files[0]);
          }}
        />

        {preview ? (
          values.image ? (
            // Novo arquivo selecionado → blob URL, exibe direto
            <div style={{ marginBottom: 10 }}>
              <img
                src={preview}
                alt={t("Preview")}
                style={{
                  width: "100%",
                  maxWidth: 420,
                  height: 150,
                  objectFit: "cover",
                  borderRadius: 8,
                  border: "1.5px solid #e8e8e8",
                }}
              />
              <button
                onClick={() => {
                  setPreview(null);
                  set("image", null);
                  setImgError("");
                }}
                style={{
                  display: "block",
                  marginTop: 6,
                  background: "none",
                  border: "none",
                  color: "#ef4444",
                  fontSize: 12,
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                {t("Remover imagem")}
              </button>
            </div>
          ) : (
            // Imagem existente do servidor → busca via Axios com token
            <BannerImage
              filename={preview}
              onRemove={() => {
                setPreview(null);
                set("image", null);
              }}
            />
          )
        ) : (
          <div
            onClick={() => fileRef.current?.click()}
            style={{
              border: "2px dashed #e8e8e8",
              borderRadius: 10,
              padding: "28px 20px",
              textAlign: "center",
              cursor: "pointer",
              background: "#fafafa",
              transition: "border-color 0.2s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.borderColor = "#00b6bc")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.borderColor = "#e8e8e8")
            }
          >
            <div style={{ color: "#ccc", marginBottom: 8 }}>
              <IconUpload />
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
              }}
            >
              <span style={{ fontSize: 13, color: "#888" }}>
                {t("Adicionar imagem")}
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  fileRef.current?.click();
                }}
                style={{
                  padding: "6px 14px",
                  background: "#e6f9fa",
                  border: "none",
                  borderRadius: 6,
                  color: "#00b6bc",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                }}
              >
                <IconUpload /> {t("Procurar")}
              </button>
            </div>
          </div>
        )}

        <p style={{ margin: "6px 0 0", fontSize: 11, color: "#aaa" }}>
          {t("Tamanho 420x150px. JPG ou PNG")}
        </p>

        {imgError && (
          <div
            style={{
              marginTop: 8,
              padding: "8px 12px",
              background: "#fde8e8",
              border: "1px solid #fca5a5",
              borderRadius: 8,
              fontSize: 12,
              color: "#ef4444",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            ⚠ {imgError}
          </div>
        )}
      </div>

      {/* ── Regras da arte ───────────────────────────────────────────────── */}
      <div
        style={{
          background: "#fafafa",
          border: "1px solid #eee",
          borderRadius: 12,
          padding: "18px 20px",
          marginBottom: 20,
        }}
      >
        <p
          style={{
            margin: "0 0 14px",
            fontSize: 14,
            fontWeight: 700,
            color: "#111",
          }}
        >
          {t("📋 Especificações técnicas")}
        </p>

        <ul
          style={{
            margin: "0 0 20px",
            paddingLeft: 18,
            fontSize: 13,
            color: "#555",
            lineHeight: 1.8,
          }}
        >
          <li>
            <strong>{t("Dimensões:")}</strong>{" "}
            {t("{w}x{h}px (exatamente esse tamanho)", {
              w: BANNER_WIDTH,
              h: BANNER_HEIGHT,
            })}
          </li>
          <li>
            <strong>{t("Peso do arquivo:")}</strong>{" "}
            {t("até {kb} KB — arquivos maiores deixam o carregamento do app lento", {
              kb: BANNER_MAX_KB,
            })}
          </li>
          <li>
            <strong>{t("Formato:")}</strong> {t(BANNER_FORMATS_LABEL)}
          </li>
          <li>
            <strong>{t("Zona de segurança:")}</strong>{" "}
            {t("mantenha textos e logos centralizados, para não serem cortados em telas menores")}
          </li>
        </ul>

        <p
          style={{
            margin: "0 0 14px",
            fontSize: 14,
            fontWeight: 700,
            color: "#111",
          }}
        >
          {t("🚫 Diretrizes de conteúdo")}
        </p>

        <ul
          style={{
            margin: 0,
            paddingLeft: 18,
            fontSize: 13,
            color: "#555",
            lineHeight: 1.8,
          }}
        >
          <li>
            <strong>{t("Direitos autorais:")}</strong>{" "}
            {t("não use imagens do Google, logos de outras marcas ou fotos de terceiros sem autorização")}
          </li>
          <li>
            <strong>{t("Textos curtos:")}</strong>{" "}
            {t("o texto não deve ocupar mais de 20% da imagem")}
          </li>
          <li>
            <strong>{t("Sem promessas falsas:")}</strong>{" "}
            {t('não inclua selos inventados (ex.: "O melhor do app") nem preços que não condizem com o cardápio')}
          </li>
          <li>
            <strong>{t("Conteúdo proibido:")}</strong>{" "}
            {t("imagens agressivas, com nudez ou gestos obscenos não são aprovadas")}
          </li>
        </ul>

        <p
          style={{
            margin: "16px 0 0",
            fontSize: 12,
            color: "#999",
            lineHeight: 1.6,
          }}
        >
          {t("Artes fora dessas regras são recusadas na análise, e você recebe o motivo por e-mail para reenviar. O prazo contratado só começa a contar após a aprovação.")}
        </p>
      </div>

      {/* ── Oferta de criação de arte ────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
          background: "#e6f9fa",
          border: "1px solid #cdf3f5",
          borderRadius: 12,
          padding: "14px 16px",
          marginBottom: 20,
        }}
      >
        <span style={{ fontSize: 20, lineHeight: 1 }}>🎨</span>
        <p
          style={{
            margin: 0,
            fontSize: 13,
            color: "#0b6f73",
            lineHeight: 1.7,
          }}
        >
          {t("Quer que o time Pertalks crie as artes para você?")}{" "}
          <a
            href={`mailto:${ART_EMAIL}?subject=${encodeURIComponent(
              t("Criação de arte para banner"),
            )}&body=${encodeURIComponent(
              `${t("Olá! Gostaria que o time Pertalks criasse a arte do meu banner.")}\n\n` +
                `${t("Nome do estabelecimento:")}\n` +
                `${t("Ideia / mensagem que quero transmitir:")}\n` +
                t("Prazo desejado:"),
            )}`}
            style={{
              color: "#0b6f73",
              fontWeight: 700,
              textDecoration: "underline",
            }}
          >
            {t("Entre em contato conosco")}
          </a>
          .
        </p>
      </div>

      {/* Actions */}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <button
          onClick={() => navigate("/establishment/banners")}
          style={{
            padding: "10px 24px",
            background: "#fff",
            border: "1.5px solid #e0e0e0",
            borderRadius: 10,
            cursor: "pointer",
            fontWeight: 600,
            fontSize: 13,
            color: "#555",
          }}
        >
          {t("Voltar")}
        </button>
        <button
          onClick={handleSubmit}
          disabled={saving}
          style={{
            padding: "10px 24px",
            background: "#00b6bc",
            border: "none",
            borderRadius: 10,
            cursor: "pointer",
            fontWeight: 700,
            fontSize: 13,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            gap: 7,
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? (
            <CircularProgress size={14} style={{ color: "#fff" }} />
          ) : (
            <IconCheck />
          )}
          {t("Salvar")}
        </button>
      </div>
    </div>
  );
};